import { ButtonBase } from '@mui/material';
import Grid from '@mui/material/Grid';
import Image from 'next/image';
import { useRouter } from 'next/router';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import { SpacingContainer } from '@/components/SpacingContainer/SpacingContainer';
import type { ProductGridAdapted } from '@/contentful/api/enhancePages/standardPageHybrid.types';
import useGlobalStyles from '@/styles/global.styles';
import { formatUSD } from '@/utils/currency';
import shopifyImageLoader from '@/utils/imageLoaders/shopify';
import Heading from '../Heading/Heading';
import useStyles from './ProductCatalogList.styles';

type ProductCatalogListProps = {
  content: ProductGridAdapted;
};

const ProductCatalogList = (props: ProductCatalogListProps) => {
  const {
    content: { fields },
  } = props;
  const gridValues = fields;
  const { darkMode } = fields;
  const { classes, cx } = useStyles({ isOnDark: darkMode });
  const router = useRouter();
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  return (
    <SpacingContainer
      isFeatured
      className={cx(classes.spacingContainer, { darkMode })}
      data-testid='productCatalogList'
    >
      <GridContainer fixed>
        <Grid size={12}>
          {gridValues.heading && (
            <Heading level='h2' className={cx(classes.heading, { darkMode })}>
              {gridValues.heading}
            </Heading>
          )}
        </Grid>
        <Grid size={12}>
          {gridValues.subhead && (
            <h2
              className={cx(globalClasses.bodyLg, classes.subhead, {
                darkMode,
              })}
            >
              {gridValues.subhead}
            </h2>
          )}
        </Grid>
        {gridValues.products.map((product, idx: number) => {
          if (!product) {
            return null;
          }
          const {
            title,
            featuredImage,
            minPrice,
            sunrunProductPageUrl,
            availableForSale,
          } = product || {};
          const isOutOfStock = availableForSale === false;
          const price = minPrice
            ? formatUSD(minPrice, 2, 2)
            : 'Price not available';

          return (
            <Grid
              key={`${title}-${price}`}
              className={classes.cardWrapper}
              data-testid='product-card'
              size={{
                xs: 6,
                sm: 6,
                md: 4,
              }}
            >
              <ButtonBase
                onClick={() => router.push(sunrunProductPageUrl)}
                data-testid={`${title}-${idx}`}
                className={classes.productCard}
                disableRipple
              >
                <div className={classes.productImageWrapper}>
                  {featuredImage && (featuredImage.url as string) && (
                    <Image
                      src={featuredImage.url as string}
                      alt={featuredImage.altText || title}
                      className={classes.productImage}
                      width={500}
                      height={500}
                      loader={shopifyImageLoader}
                    />
                  )}
                </div>
                <div className={classes.textContainer}>
                  <h3
                    className={cx(globalClasses.bodyLg, classes.productTitle, {
                      darkMode,
                    })}
                  >
                    {title}
                  </h3>
                  <p
                    className={cx(globalClasses.bodyLg, classes.productPrice, {
                      darkMode,
                    })}
                  >
                    {price}
                  </p>
                  {isOutOfStock && (
                    <span
                      className={cx(
                        globalClasses.eyebrow,
                        classes.outOfStockChip
                      )}
                    >
                      Out of stock
                    </span>
                  )}
                </div>
              </ButtonBase>
            </Grid>
          );
        })}
      </GridContainer>
    </SpacingContainer>
  );
};

export default ProductCatalogList;
